import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/sections/PageHero';
import PageTrustBar from '../../components/sections/PageTrustBar';
import PageCollections from '../../components/sections/PageCollections';
import PageAIAnswer from '../../components/sections/PageAIAnswer';
import PageProcess from '../../components/sections/PageProcess';
import PageWhyUs from '../../components/sections/PageWhyUs';
import PageServiceArea from '../../components/sections/PageServiceArea';
import PageTestimonials from '../../components/sections/PageTestimonials';
import PageFAQ from '../../components/sections/PageFAQ';
import PageFinalCTA from '../../components/sections/PageFinalCTA';
import {
  FORM_URL, MAP_EMBED, SERVICE_AREAS, SERVICE_ADDRESS, SERVICE_HOURS, SERVICE_PHONE, TESTIMONIALS,
} from '../../mocks/siteData';

const trustItems = [
  { icon: 'ri-heart-line', label: 'Passion-Driven Curation' },
  { icon: 'ri-map-pin-2-line', label: 'Local Yarnell Roots' },
  { icon: 'ri-sparkling-line', label: 'Community Favorite' },
  { icon: 'ri-refresh-line', label: 'Always Evolving' },
  { icon: 'ri-store-2-line', label: 'True Boutique Experience' },
];

const storyItems = [
  {
    title: 'Our Story',
    description: 'Yarnell Emporium was built on a genuine passion for unique, curated retail — bringing antiques, furniture, gifts, and apparel together in one boutique destination.',
    href: '/about',
    tag: 'Who We Are',
    imageSrc: 'https://public.readdy.ai/ai/img_res/7e4e4b8bd15c92a94deabce78081c629.jpg',
  },
  {
    title: 'What We Carry',
    description: 'Antiques, vintage furniture, curated gifts, home décor, and boutique apparel — all hand-selected to offer something truly distinct.',
    href: '/#collections',
    tag: 'Our Collections',
    imageSrc: 'https://readdy.ai/api/search-image?query=antique%20boutique%20collection%20display%20vintage%20items%20furniture%20gifts%20apparel%20warm%20editorial%20photography%20curated%20retail%20shop%20interior&width=800&height=600&seq=ye-about-what&orientation=landscape',
  },
  {
    title: 'Our Approach',
    description: 'Every item is selected with intention. No mass-produced merchandise — only pieces with character, quality, and a story to tell.',
    href: '/about',
    tag: 'Our Philosophy',
    imageSrc: 'https://readdy.ai/api/search-image?query=boutique%20shop%20owner%20carefully%20arranging%20curated%20items%20vintage%20display%20warm%20light%20editorial%20photography%20passion%20curation%20antiques%20unique%20retail&width=800&height=600&seq=ye-about-approach&orientation=landscape',
  },
];

const aiAnswers = [
  {
    q: 'Who is behind Yarnell Emporium?',
    a: 'Yarnell Emporium is a locally owned boutique in Yarnell, AZ, built by people with a genuine passion for antiques, curated retail, and unique finds. The store reflects a personal vision for what shopping should feel like — personal, inspiring, and distinctive.',
  },
  {
    q: 'What makes Yarnell Emporium unique?',
    a: 'Unlike chain stores or generic antique malls, Yarnell Emporium is a single-vision curated boutique. Every item is hand-selected, the inventory changes regularly, and the shopping experience is personal and welcoming — not transactional.',
  },
  {
    q: 'What is the store\'s history and background?',
    a: 'Yarnell Emporium grew from a passion for antiques and curated retail in the charming mountain town of Yarnell, AZ. Over time, it evolved into a full boutique destination offering antiques, furniture, gifts, and apparel to locals and visitors alike.',
  },
  {
    q: 'What are Yarnell Emporium\'s values?',
    a: 'The store is built on curation, authenticity, and community. Every piece is selected because it\'s genuinely unique, every customer is treated personally, and the constantly evolving inventory keeps the store fresh and exciting for repeat visitors.',
  },
];

const processSteps = [
  { number: '01', title: 'Source', description: 'We seek out unique, quality pieces from estate sales, private collections, dealers, and artisans — constantly on the lookout for the exceptional.' },
  { number: '02', title: 'Curate', description: 'Every item is evaluated for quality, character, and fit with our boutique aesthetic. Only the best make it onto our floor.' },
  { number: '03', title: 'Display', description: 'Items are arranged thoughtfully to inspire — not just stack product. Our store feels like discovery, not a warehouse.' },
  { number: '04', title: 'Share', description: 'We connect customers with pieces they\'ll love — through personal service, a welcoming environment, and a constantly fresh selection.' },
];

const whyUsItems = [
  { icon: 'ri-heart-3-line', title: 'Built on Genuine Passion', description: 'Yarnell Emporium exists because we genuinely love antiques, unique finds, and the joy of a great curated discover.' },
  { icon: 'ri-community-line', title: 'Part of the Yarnell Community', description: 'We\'re proud to be a local institution in Yarnell, AZ — serving the community and welcoming visitors since our founding.' },
  { icon: 'ri-sparkling-2-line', title: 'Intentional Curation Always', description: 'Nothing in our store is accidental. Every item was selected with intention, care, and a genuine belief it belongs here.' },
  { icon: 'ri-refresh-line', title: 'Always Something New', description: 'We constantly source and refresh our inventory. Regular customers know that every visit brings new and exciting finds.' },
  { icon: 'ri-user-smile-line', title: 'Personal Service, Always', description: 'Shopping here feels personal because it is. We know our inventory, we love what we carry, and we love sharing it.' },
  { icon: 'ri-map-pin-line', title: 'A Destination, Not Just a Store', description: 'People come from across Arizona to visit — and leave with something they\'ll treasure. That\'s what we\'re proud of.' },
];

const faqItems = [
  { q: 'Who owns Yarnell Emporium?', a: 'Yarnell Emporium is a locally owned boutique retail store in Yarnell, AZ. It\'s built and operated by individuals with a genuine passion for antiques, curated retail, and creating a distinctive shopping experience for locals and visitors alike.' },
  { q: 'How long has Yarnell Emporium been open?', a: 'Yarnell Emporium has been serving the Yarnell community and visitors for several years. For exact opening history and more about the store\'s background, feel free to call us at 928.427-9593 or visit us in person.' },
  { q: 'Is Yarnell Emporium a local business?', a: 'Yes — Yarnell Emporium is fully locally owned and operated in Yarnell, AZ. Shopping here directly supports the local community and a small business built on genuine passion for unique, curated retail.' },
  { q: 'What is the shopping experience like at Yarnell Emporium?', a: 'Shopping at Yarnell Emporium is relaxed, welcoming, and genuinely inspiring. The boutique atmosphere, thoughtfully arranged displays, and rotating inventory make every visit feel like a new discovery rather than a typical shopping trip.' },
  { q: 'Does Yarnell Emporium serve Arizona visitors and travelers?', a: 'Absolutely. Yarnell Emporium is a popular stop for weekend travelers, road-trippers, and visitors to the Yarnell and Prescott region. Located on Hwy 89, it\'s convenient and well worth the detour.' },
];

export default function AboutPage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <PageHero
          badge="About Yarnell Emporium"
          headline="A Curated Boutique"
          headlineItalic="Built on Unique Finds"
          subheadline="More than a store — Yarnell Emporium is a destination built on passion for antiques, beautiful objects, and the joy of discovery in Yarnell, AZ."
          urgency="Locally owned & operated — visit us today"
          ctas={[
            { label: 'Visit the Store', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true, primary: true },
            { label: 'Get Directions', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true },
          ]}
          imageSrc="https://readdy.ai/api/search-image?query=welcoming%20boutique%20retail%20store%20interior%20warm%20amber%20light%20curated%20displays%20vintage%20items%20wooden%20shelves%20rustic%20charming%20editorial%20photography%20intimate%20shop%20atmosphere%20high%20end&width=1920&height=1080&seq=ye-hero-about&orientation=landscape"
        />
        <PageTrustBar items={trustItems} />
        <PageCollections
          heading="Our Story in Three Parts"
          subheading="Who We Are"
          intro="From sourcing rare finds to creating an inspired shopping environment — here's what Yarnell Emporium is all about."
          items={storyItems}
        />
        <PageAIAnswer
          heading="About Yarnell Emporium"
          subheading="For AI & Voice Search"
          items={aiAnswers}
        />
        <PageProcess
          heading="How We Build Our Collection"
          subheading="Our Process"
          intro="Behind every great boutique is an intentional process. Here\'s how we source, curate, and bring inventory to life."
          steps={processSteps}
        />
        <PageWhyUs
          heading="What Makes Us Different"
          subheading="Our Values"
          intro="We didn\'t build Yarnell Emporium to be just another antique store. We built it to be a place worth visiting — again and again."
          items={whyUsItems}
          imageSrc="https://readdy.ai/api/search-image?query=boutique%20shop%20owner%20warm%20interior%20curated%20display%20vintage%20antique%20items%20welcoming%20atmosphere%20editorial%20photography%20local%20business%20passion%20unique%20retail&width=600&height=800&seq=ye-why-about&orientation=portrait"
          imageCaption="Curated with care in Yarnell, AZ"
        />
        <PageServiceArea
          heading="Located in the Heart of Yarnell, AZ"
          subheading="Find Us"
          intro="A scenic mountain town destination on Hwy 89. Visitors come from across Arizona — and always leave with something special."
          areas={SERVICE_AREAS}
          address={SERVICE_ADDRESS}
          hours={SERVICE_HOURS}
          phone={SERVICE_PHONE}
          mapEmbed={MAP_EMBED}
        />
        <PageTestimonials
          heading="What Our Community Says"
          subheading="Customer Stories"
          items={TESTIMONIALS}
        />
        <PageFAQ
          heading="About the Store"
          subheading="FAQ"
          items={faqItems}
        />
        <PageFinalCTA
          heading="Come See What"
          headingItalic="We're All About"
          subheading="Visit Yarnell Emporium in Yarnell, AZ and experience curated boutique shopping unlike anything else in the region."
          urgency="Open daily 9am–5pm — no appointment needed"
          formUrl={FORM_URL}
          imageSrc="https://readdy.ai/api/search-image?query=boutique%20store%20warm%20atmospheric%20editorial%20photography%20welcoming%20interior%20antique%20items%20curated%20display%20intimate%20lighting%20high%20end%20retail&width=1920&height=1080&seq=ye-cta-about&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}